import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SupabaseService } from '../features/supabase/supabase.service';

/** Marked on the post-refresh retry to avoid infinite refresh loops. */
const supabaseJwtRefreshDone = new HttpContextToken<boolean>(() => false);

function wantsSupabaseBearer(url: string): boolean {
  if (url.startsWith('/api/')) return true;
  const base = environment.masterServerUrl?.replace(/\/+$/, '') ?? '';
  return !!(base && url.startsWith(base));
}

/**
 * Sends the current Supabase access token with same-origin `/api/*` and master-server calls.
 * On 401, attempts one refresh and retries (without re-entering interceptors upstream).
 */
export const apiAuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (!wantsSupabaseBearer(req.url)) {
    return next(req);
  }

  const supabase = inject(SupabaseService);
  const bearer = supabase.token();

  let out = req;
  if (bearer) {
    out = req.clone({
      setHeaders: { Authorization: `Bearer ${bearer}` },
    });
  }

  return next(out).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (req.context.get(supabaseJwtRefreshDone)) {
        return throwError(() => err);
      }

      return from(supabase.client.auth.refreshSession()).pipe(
        switchMap(({ data: { session }, error }) => {
          if (error || !session?.access_token) {
            return throwError(() => err);
          }
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${session.access_token}` },
            context: req.context.set(supabaseJwtRefreshDone, true),
          });
          return next(retried);
        }),
      );
    }),
  );
};
